import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allocateCredits, listUserCredits, UserCreditRow } from '../../services/CreditsService';
import { getTiers } from '../../services/SubscriptionTierService';
import type { SubscriptionTier } from '../../types/subscriptionTier';

interface ClientView {
  id: string;
  name: string;
  plan: string;
  allocated: number;
  used: number;
  remaining: number;
}

const AllocateCreditsPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [clients, setClients] = useState<ClientView[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const rows: UserCreditRow[] = await listUserCredits();
        setClients(rows.map(r => ({ id: r.userId, name: r.client, plan: r.plan || '—', allocated: r.allocated, used: r.used, remaining: r.remaining })));
      } catch (e) {
        console.error(e);
        setErrorMsg('Failed to load clients');
        setTimeout(() => setErrorMsg(''), 5000);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadTiers = async () => {
      try {
        const t = await getTiers();
        setTiers(t);
      } catch (e) {
        console.error('Failed to load tiers', e);
      }
    };
    loadTiers();
  }, []);

  const [selectedClient, setSelectedClient] = useState<string>('');
  const [creditsToAdd, setCreditsToAdd] = useState<number>(0);
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [pricePerCreditUsd, setPricePerCreditUsd] = useState<number>(0);
  const [messageLengthSymbols] = useState<number>(160);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [selectedPlanName, setSelectedPlanName] = useState<string>('');

  const handleSaveAllocation = async () => {
    if (!selectedClient || creditsToAdd <= 0) return;
    try {
      await allocateCredits({ userId: selectedClient, amount: creditsToAdd, reason: notes || undefined, expiresAt: expiryDate || undefined });
      setSuccessMsg('Credits allocated successfully.');
      setErrorMsg('');
      setTimeout(() => setSuccessMsg(''), 4000);
      // refresh list and selected details
      const rows: UserCreditRow[] = await listUserCredits();
      setClients(rows.map(r => ({ id: r.userId, name: r.client, plan: r.plan || '—', allocated: r.allocated, used: r.used, remaining: r.remaining })));
      setCreditsToAdd(0);
      setExpiryDate('');
      setNotes('');
    } catch (e) {
      console.error(e);
      setSuccessMsg('');
      setErrorMsg(e instanceof Error ? e.message : 'Failed to allocate credits');
      setTimeout(() => setErrorMsg(''), 5000);
    }
  };

  const selectedClientData = useMemo(() => clients.find(client => client.id === selectedClient), [clients, selectedClient]);

  useEffect(() => {
    if (selectedClientData) {
      setSelectedPlanName(selectedClientData.plan);
    } else {
      setSelectedPlanName('');
    }
  }, [selectedClientData]);

  return (
    <div className="max-w-4xl mx-auto">
      {successMsg ? (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">{successMsg}</div>
      ) : null}
      {errorMsg ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-800">{errorMsg}</div>
      ) : null}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Allocate Credits</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/billing')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAllocation}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
            disabled={!selectedClient || creditsToAdd <= 0 || loading}
          >
            Save Allocation
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Client Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Client
          </label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">Select a client...</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name} ({client.plan} Plan)
              </option>
            ))}
          </select>
        </div>

        {/* Enhanced Client Information */}
        {selectedClientData && (
          <div className="space-y-6">
            {/* Credit Usage Stats */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Credit Usage</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="text-xs text-gray-500 uppercase">Total Allocated</h4>
                  <p className="text-lg font-medium text-gray-900">
                    {selectedClientData.allocated.toLocaleString()}
                  </p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 uppercase">Credits Used</h4>
                  <p className="text-lg font-medium text-red-600">
                    {selectedClientData.used.toLocaleString()}
                  </p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 uppercase">Credits Remaining</h4>
                  <p className="text-lg font-medium text-[#FF5722]">
                    {selectedClientData.remaining.toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Usage Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Usage</span>
                  <span>{((selectedClientData.allocated ? selectedClientData.used / selectedClientData.allocated : 0) * 100).toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-[#FF5722] rounded-full" 
                    style={{ 
                      width: `${(selectedClientData.allocated ? (selectedClientData.used / selectedClientData.allocated) * 100 : 0)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Credit Allocation Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Credits to Add
            </label>
            <input
              type="number"
              value={creditsToAdd}
              onChange={(e) => setCreditsToAdd(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Enter number of credits"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiry Date
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sale Price per Credit (USD)
            </label>
            <div className="flex items-center">
              <span className="px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-600">$
              </span>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={pricePerCreditUsd}
                onChange={(e) => setPricePerCreditUsd(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-r-md px-3 py-2"
                placeholder="0.0000"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Length for One Credit
            </label>
            <input
              type="number"
              value={messageLengthSymbols}
              disabled
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">Total of 160 symbols counts as one credit.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Package
            </label>
            <select
              value={selectedPlanName}
              onChange={(e) => setSelectedPlanName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select package...</option>
              {(tiers.length ? tiers.map(t => t.name) : ['Basic','Professional','Enterprise']).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {selectedClientData?.plan && (
              <p className="text-xs text-gray-500 mt-1">Current plan: {selectedClientData.plan}</p>
            )}
          </div>
        </div>

        {/* Summary */}
        {selectedClientData && creditsToAdd > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-3">Allocation Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Credits</span>
                <span className="font-medium">{selectedClientData.remaining.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Credits to Add</span>
                <span className="font-medium text-green-600">+{creditsToAdd.toLocaleString()}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-900 font-medium">New Total</span>
                <span className="font-medium text-[#FF5722]">
                  {(selectedClientData.remaining + creditsToAdd).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            placeholder="Add any additional notes..."
          />
        </div>
      </div>
    </div>
  );
};

export default AllocateCreditsPage; 